import React, { useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import { LoadingButton } from '@mui/lab';
import { mediaService } from '../../api';
import {
  useCreateBankLookupMutation,
  useUpdateBankLookupMutation,
} from '../../redux/features/bankLookup/bankLookup.apiSlice';
import { BankLookup, CreateBankLookupRequest } from '../../types/bankLookup';

const LOGO_UPLOAD_ENTITY = 'bankLogo';
const LOGO_ACCEPT = 'image/png,image/jpeg,image/svg+xml,.png,.jpg,.jpeg,.svg';
const LOGO_SIZE = 80;

type FormValues = {
  bankName: string;
  bankNameAr: string;
  countryName: string;
  countryNameAr: string;
  bic: string;
  bankCode: string;
  logoUrl: string;
};

const toFormValues = (bank: BankLookup | null): FormValues => ({
  bankName: bank?.bankName || '',
  bankNameAr: bank?.bankNameAr || '',
  countryName: bank?.countryName || '',
  countryNameAr: bank?.countryNameAr || '',
  bic: bank?.bic || '',
  bankCode: bank?.bankCode || '',
  logoUrl: bank?.logoUrl || '',
});

/** Trimmed values; a blank bank code or logo is sent as null to clear it, a blank BIC is left out. */
const toRequest = (values: FormValues): CreateBankLookupRequest => {
  const bic = values.bic.trim().toUpperCase();

  return {
    bankName: values.bankName.trim(),
    bankNameAr: values.bankNameAr.trim(),
    countryName: values.countryName.trim(),
    countryNameAr: values.countryNameAr.trim(),
    ...(bic ? { bic } : {}),
    bankCode: values.bankCode.trim() || null,
    logoUrl: values.logoUrl || null,
  };
};

const buildSchema = (bicRequired: boolean) =>
  yup.object({
    bankName: yup.string().trim().required('English name is required'),
    bankNameAr: yup.string().trim().required('Arabic name is required'),
    countryName: yup.string().trim().required('English country is required'),
    countryNameAr: yup.string().trim().required('Arabic country is required'),
    bic: bicRequired ? yup.string().trim().required('The BIC can be changed but not removed') : yup.string(),
    bankCode: yup
      .string()
      .trim()
      .matches(/^\d{2}$/, 'Bank code must be exactly 2 digits'),
  });

/** Maps the server's error codes to the field they belong to, or to a message for the whole form. */
const describeSaveError = (error: any): { field?: keyof FormValues; message: string } => {
  switch (error?.data?.message) {
    case 'BANK_BIC_ALREADY_EXISTS':
      return { field: 'bic', message: 'Another bank already has this BIC.' };
    case 'BANK_CODE_ALREADY_EXISTS':
      return { field: 'bankCode', message: 'Another bank already has this bank code.' };
    case 'BANK_LOOKUP_NOT_FOUND':
      return { message: 'This bank no longer exists. It may have been deleted by someone else.' };
    default:
      return { message: error?.data?.message || 'The bank could not be saved.' };
  }
};

interface BankLookupDialogProps {
  open: boolean;
  /** The bank to edit, or null to add one. */
  bank: BankLookup | null;
  /** Only the logo can be changed; the other fields are shown read-only and saved as they are. */
  logoOnly?: boolean;
  onClose: () => void;
}

export function BankLookupDialog({ open, bank, logoOnly = false, onClose }: BankLookupDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createBank, { isLoading: isCreating }] = useCreateBankLookupMutation();
  const [updateBank, { isLoading: isUpdating }] = useUpdateBankLookupMutation();
  const [saveError, setSaveError] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const creating = !bank;

  const { values, touched, errors, handleChange, handleBlur, setFieldValue, setFieldError, submitForm, resetForm } =
    useFormik<FormValues>({
      initialValues: toFormValues(bank),
      enableReinitialize: true,
      validationSchema: logoOnly ? undefined : buildSchema(Boolean(bank?.bic)),
      onSubmit: async (formValues) => {
        setSaveError('');

        try {
          if (creating) {
            await createBank(toRequest(formValues)).unwrap();
          } else {
            const next = logoOnly ? { ...toFormValues(bank), logoUrl: formValues.logoUrl } : formValues;

            await updateBank({ id: bank.id, ...toRequest(next) }).unwrap();
          }

          enqueueSnackbar(creating ? 'Bank added.' : 'Bank saved.', { variant: 'success' });
          resetForm();
          onClose();
        } catch (err) {
          const { field, message } = describeSaveError(err);

          if (field) {
            setFieldError(field, message);
          } else {
            setSaveError(message);
          }
        }
      },
    });

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (logoInputRef.current) logoInputRef.current.value = '';
    if (!file) return;

    setLogoUploading(true);

    try {
      const response = await mediaService.uploadAnonymousPublic(file, LOGO_UPLOAD_ENTITY);

      if (response?.data?.url) {
        setFieldValue('logoUrl', response.data.url);
      } else {
        enqueueSnackbar('The logo could not be uploaded.', { variant: 'error' });
      }
    } catch (err) {
      console.error('Bank logo upload error:', err);
      enqueueSnackbar('The logo could not be uploaded. Use a PNG, JPG or SVG image.', { variant: 'error' });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleClose = () => {
    setSaveError('');
    setLogoUploading(false);
    resetForm();
    onClose();
  };

  const fieldProps = (name: keyof FormValues) => ({
    id: `bank-lookup-${name}`,
    name,
    value: values[name],
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] && Boolean(errors[name]),
    helperText: touched[name] && errors[name],
    margin: 'normal' as const,
    fullWidth: true,
    disabled: logoOnly,
  });
  const logoUnchanged = logoOnly && values.logoUrl === (bank?.logoUrl || '');

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {creating && 'Add a bank'}
        {!creating && (logoOnly ? `Edit logo: ${bank.bankName}` : `Edit ${bank.bankName}`)}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <input
            ref={logoInputRef}
            type="file"
            accept={LOGO_ACCEPT}
            onChange={handleLogoChange}
            style={{ display: 'none' }}
            id="bank-lookup-logo"
          />
          <label htmlFor="bank-lookup-logo" style={{ cursor: logoUploading ? 'wait' : 'pointer' }}>
            <Box
              component="span"
              sx={{
                width: LOGO_SIZE,
                height: LOGO_SIZE,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                bgcolor: values.logoUrl ? 'transparent' : 'action.hover',
                pointerEvents: logoUploading ? 'none' : 'auto',
                '&:hover': logoUploading ? {} : { borderColor: 'primary.main', bgcolor: 'action.selected' },
              }}
            >
              {logoUploading && <CircularProgress size={36} />}
              {values.logoUrl && !logoUploading && (
                <Avatar
                  src={values.logoUrl}
                  alt="Logo"
                  variant="rounded"
                  sx={{ width: LOGO_SIZE, height: LOGO_SIZE, '& img': { objectFit: 'contain' } }}
                />
              )}
              {!values.logoUrl && !logoUploading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <UploadIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Select logo
                  </Typography>
                </Box>
              )}
            </Box>
          </label>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {logoUploading && 'Uploading…'}
              {!logoUploading && (values.logoUrl ? 'Click the logo to replace it.' : 'PNG, JPG or SVG.')}
            </Typography>
            {values.logoUrl && !logoUploading && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setFieldValue('logoUrl', '')}
                sx={{ mt: 1 }}
              >
                Remove
              </Button>
            )}
          </Box>
        </Box>

        <Grid container columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps('bankName')} required label="Name En" inputProps={{ dir: 'ltr' }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps('bankNameAr')} required label="Name Ar" inputProps={{ dir: 'rtl' }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps('countryName')} required label="Country En" inputProps={{ dir: 'ltr' }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps('countryNameAr')} required label="Country Ar" inputProps={{ dir: 'rtl' }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...fieldProps('bic')}
              required={Boolean(bank?.bic)}
              label="BIC"
              placeholder="BSFRSARI"
              inputProps={{ dir: 'ltr', maxLength: 11, style: { textTransform: 'uppercase' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...fieldProps('bankCode')}
              label="Bank code"
              placeholder="55"
              helperText={(touched.bankCode && errors.bankCode) || '2 digits. Leave blank if not confirmed yet.'}
              inputProps={{ dir: 'ltr', maxLength: 2, inputMode: 'numeric' }}
            />
          </Grid>
        </Grid>

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={isCreating || isUpdating}
          loadingPosition="end"
          disabled={logoUploading || logoUnchanged}
          onClick={submitForm}
        >
          {creating ? 'Add' : 'Save'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
